import React from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { TypeBreakdown } from "../../models/models";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

ChartJS.register(ArcElement, Tooltip, Legend);

interface TypeDistributionChartProps {
  byType: TypeBreakdown[];
}

const TYPE_COLORS: Record<number, string> = {
  1: "#EF4444", // Reactive — red
  2: "#3B82F6", // Preventive — blue
  3: "#8B5CF6", // Predictive — purple
  4: "#F59E0B", // Inspection — amber
  5: "#6B7280", // General — gray
};

const TypeDistributionChart: React.FC<TypeDistributionChartProps> = ({ byType }) => {
  const { translate } = useTranslation("operations");

  const total = byType.reduce((sum, t) => sum + t.count, 0);

  const data = {
    labels: byType.map(t => t.typeName),
    datasets: [
      {
        data: byType.map(t => t.count),
        backgroundColor: byType.map(t => TYPE_COLORS[t.typeId] || "#9CA3AF"),
        borderWidth: 0,
        hoverOffset: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          font: { family: "var(--font-sans)", size: 12 },
          color: dt.textSecondary,
          usePointStyle: true,
          pointStyle: "circle" as const,
          padding: 12,
          generateLabels: (chart: any) => {
            const ds = chart.data.datasets[0];
            return chart.data.labels.map((label: string, i: number) => ({
              text: `${label} (${ds.data[i]})`,
              fillStyle: ds.backgroundColor[i],
              hidden: false,
              index: i,
              pointStyle: "circle" as const,
            }));
          },
        },
      },
      tooltip: {
        backgroundColor: "#1a1a2e",
        titleFont: { family: "var(--font-sans)", size: 13 },
        bodyFont: { family: "var(--font-mono)", size: 12 },
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: (ctx: any) => {
            const pct = total > 0 ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
            return `${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
  };

  const centerTextPlugin = {
    id: "centerText",
    afterDraw: (chart: any) => {
      const { ctx, width, height } = chart;
      const meta = chart.getDatasetMeta(0);
      if (!meta || !meta.data || meta.data.length === 0) return;

      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Center of the doughnut
      const centerX = (meta.data[0].x || width / 2);
      const centerY = (meta.data[0].y || height / 2);

      ctx.font = `700 24px var(--font-mono)`;
      ctx.fillStyle = dt.textPrimary;
      ctx.fillText(total.toString(), centerX, centerY - 8);

      ctx.font = `500 11px var(--font-sans)`;
      ctx.fillStyle = dt.textMuted;
      ctx.fillText("Total", centerX, centerY + 12);

      ctx.restore();
    },
  };

  return (
    <Box
      sx={{
        background: dt.cardBg,
        borderRadius: dt.radius,
        border: `1px solid ${dt.cardBorder}`,
        boxShadow: dt.cardShadow,
        p: 2.5,
      }}
    >
      <Typography
        sx={{
          fontFamily: dt.font,
          fontSize: "15px",
          fontWeight: 600,
          color: dt.textPrimary,
          mb: 2,
        }}
      >
        {translate("analytics.typeDistribution")}
      </Typography>
      <Box sx={{ height: 280 }}>
        {byType.length > 0 ? (
          <Doughnut data={data} options={options} plugins={[centerTextPlugin]} />
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
              {translate("analytics.noData")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TypeDistributionChart;
